import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useMutation } from '@tanstack/react-query';

import { getAccessToken } from 'selectors/entities/authentication/authentication';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import {
  getCurrentLocale,
  getFieldIdsAndValues,
  getSelectedOffer,
} from 'selectors/ui/ui';

import type { RecurringOffer } from 'models/offer';
import type { RootState } from 'reducers/types';
import { getFieldByEntity } from 'selectors/entities/fields/fields';
import {
  type CheckoutSessionType,
  type CreateSessionParams,
  type DirectDebitProcessor,
  sessions,
} from 'services/sessions/sessions';

type UseCreateCheckoutSessionProps = {
  paymentMethodToken?: string;
  sessionType: CheckoutSessionType;
};

const useCreateCheckoutSession = ({
  paymentMethodToken,
  sessionType,
}: UseCreateCheckoutSessionProps) => {
  const accessToken = useSelector(getAccessToken);
  const locale = useSelector(getCurrentLocale);
  const recipientId = useSelector(getRecipientId);
  const { ids } = useSelector((state: RootState) =>
    getFieldByEntity(state, 'sender'),
  );
  const senderFields = useSelector((state: RootState) =>
    getFieldIdsAndValues(state, ids),
  );
  const offer = useSelector(getSelectedOffer) as RecurringOffer;

  const payor = {
    firstName: senderFields.sender_first_name as string,
    lastName: senderFields.sender_last_name as string,
    address: senderFields.sender_address1 as string,
    city: senderFields.sender_city as string,
    country: senderFields.sender_country as string,
    email: senderFields.sender_email as string,
    phone: senderFields.sender_phone as string,
    state: senderFields.sender_state as string,
    zip: senderFields.sender_zip ? (senderFields.sender_zip as string) : '',
  };

  const [createSessionError, setCreateSessionError] = useState<unknown>();

  const {
    mutateAsync: createCheckoutSession,
    data: session,
    isError,
  } = useMutation(
    [`checkoutCreateSession`],
    async (params: CreateSessionParams) => sessions.create(params),
  );

  const mandateAch =
    sessionType === 'direct_debit_tokenization_for_installment' &&
    (offer.paymentMethodProcessor as DirectDebitProcessor) === 'CRBA'
      ? {
          initDate: offer.installments[0].date,
          finalDate: offer.installments[offer.installments.length - 1].date,
          numberOfPayments: offer.installments.length,
        }
      : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const createSession = useCallback(() => {
    return createCheckoutSession({
      accessToken,
      directDebitProcessor:
        sessionType === 'direct_debit_tokenization_for_installment'
          ? (offer.paymentMethodProcessor as DirectDebitProcessor)
          : undefined,
      locale,
      mandateAch,
      paymentMethodToken,
      recipientId,
      payor:
        sessionType === 'direct_debit_tokenization_for_installment'
          ? payor
          : undefined,
      sessionType,
    });
  }, [
    accessToken,
    createCheckoutSession,
    locale,
    paymentMethodToken,
    recipientId,
    sessionType,
    payor?.address,
    offer.paymentMethodProcessor,
  ]);

  useEffect(() => {
    async function createCheckoutSession() {
      try {
        await createSession();
      } catch (error) {
        setCreateSessionError(error);
      }
    }

    createCheckoutSession();
  }, [createSession]);

  return {
    createSession,
    createSessionError: createSessionError || isError,
    sessionId: session?.id,
  };
};

export { useCreateCheckoutSession };
